import { Linking, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

// WEB-ONLY route for `/` — a static marketing landing page.
//
// This file is load-bearing beyond marketing: the native `index.tsx` imports
// VisionCamera / Nitro modules / RevenueCat, and because every web route
// shares one JS bundle, bundling that screen for web crashed hydration on
// EVERY page ("__fbBatchedBridgeConfig is not set") — the privacy/support
// pages flashed their static HTML then went blank white. With this platform
// split, Metro resolves `/` to this file on web and the native-only modules
// drop out of the web bundle entirely. Keep this file free of any native-only
// imports (no vision-camera, nitro, purchases, apple-auth, notifications).

const FEATURES = [
  { title: 'Scan your face', body: 'A guided multi-angle scan, analyzed by AI across skin, jawline, eyes, cheekbones, structure, and hair.' },
  { title: 'See your potential', body: 'A realistic AI preview of you at your absolute best — the same you, dialed in. Not a filter.' },
  { title: 'Follow your plan', body: 'A personalized 8-week glow-up plan with daily habits, streaks, and rescans to track real progress.' },
];

export default function LandingScreen() {
  return (
    <ScrollView style={s.page} contentContainerStyle={s.content}>
      <Text style={s.brand}>MAXIFY</Text>
      <Text style={s.headline}>Unlock your full potential.</Text>
      <Text style={s.sub}>
        Scan your face, see a realistic preview of your best self, and get a personalized plan to actually get there.
      </Text>

      <View style={s.badgeWrap}>
        <Text style={s.badge}>Coming soon to the App Store</Text>
      </View>

      <View style={s.features}>
        {FEATURES.map((f, i) => (
          <View key={i} style={s.feature}>
            <Text style={s.featureNum}>{String(i + 1).padStart(2, '0')}</Text>
            <View style={{ flex: 1 }}>
              <Text style={s.featureTitle}>{f.title}</Text>
              <Text style={s.featureBody}>{f.body}</Text>
            </View>
          </View>
        ))}
      </View>

      <View style={s.linksRow}>
        {([
          ['Support', 'https://looksmax.expo.app/support'],
          ['Privacy Policy', 'https://looksmax.expo.app/privacy'],
          ['Terms', 'https://looksmax.expo.app/terms'],
        ] as const).map(([label, url]) => (
          <TouchableOpacity key={label} onPress={() => Linking.openURL(url)} activeOpacity={0.7}>
            <Text style={s.link}>{label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <Text style={s.footer}>Maxify is a self-improvement app — not a medical service.</Text>
    </ScrollView>
  );
}

const s = StyleSheet.create({
  page: { flex: 1, backgroundColor: '#05070A' },
  content: { maxWidth: 680, width: '100%', alignSelf: 'center', paddingHorizontal: 28, paddingTop: 96, paddingBottom: 80 },
  brand: {
    color: '#00BFFF', fontSize: 15, fontWeight: '900', letterSpacing: 6, marginBottom: 28,
  },
  headline: { color: '#EAECEF', fontSize: 44, fontWeight: '900', letterSpacing: -1, lineHeight: 50, marginBottom: 16 },
  sub: { color: '#8A9099', fontSize: 17, lineHeight: 26, marginBottom: 28, maxWidth: 520 },
  badgeWrap: { flexDirection: 'row', marginBottom: 56 },
  badge: {
    color: '#00BFFF', fontSize: 13, fontWeight: '700', letterSpacing: 0.5,
    borderWidth: 1, borderColor: 'rgba(0,191,255,0.35)', backgroundColor: 'rgba(0,191,255,0.08)',
    paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999, overflow: 'hidden',
  },
  features: { gap: 28, marginBottom: 64 },
  feature: { flexDirection: 'row', gap: 18, alignItems: 'flex-start' },
  featureNum: { color: 'rgba(0,191,255,0.55)', fontSize: 13, fontWeight: '800', letterSpacing: 1, marginTop: 3, fontVariant: ['tabular-nums'] },
  featureTitle: { color: '#EAECEF', fontSize: 18, fontWeight: '800', marginBottom: 5 },
  featureBody: { color: '#8A9099', fontSize: 14.5, lineHeight: 21, maxWidth: 480 },
  linksRow: { flexDirection: 'row', gap: 26, marginBottom: 20, flexWrap: 'wrap' },
  link: { color: 'rgba(255,255,255,0.6)', fontSize: 13.5, fontWeight: '600', textDecorationLine: 'underline' },
  footer: { color: 'rgba(255,255,255,0.3)', fontSize: 12 },
});
